import type { Ssm5Dtc, Ssm5DtcModule } from '@packages/domain/ssm5';

/** A filter of the DTC list: the DTCs it hides while switched off, and its label. */
export type Ssm5DtcFilter = {
    /** Whether the filter hides the DTC while switched off. */
    hides: (dtc: Ssm5Dtc) => boolean;
    /** The filter, as its switch is keyed. */
    key: Ssm5DtcFilterKey;
    /** The translation key of its label. */
    label: string;
};

/** A filter of the DTC list. */
export type Ssm5DtcFilterKey = 'historical' | 'secondary';

/** Which filters of the DTC list are switched on. */
export type Ssm5DtcFilters = Readonly<Record<Ssm5DtcFilterKey, boolean>>;

/** The filters of the DTC list, in the order their switches are shown. */
export const DTC_FILTERS: readonly Ssm5DtcFilter[] = [
    { hides: (dtc) => dtc.isHistory, key: 'historical', label: 'ssm5:dtcs.historical' },
    { hides: (dtc) => dtc.isSecondary, key: 'secondary', label: 'ssm5:dtcs.secondary' },
];

/** Every filter switched on, so every DTC shows. */
export const ALL_DTC_FILTERS_ON: Ssm5DtcFilters = { historical: true, secondary: true };

/**
 * Count the DTCs of the given modules.
 *
 * @param modules - the modules
 * @returns how many DTCs they hold together
 */
export function countDtcs(modules: readonly Ssm5DtcModule[]): number {
    return modules.reduce((count, module) => count + module.dtcs.length, 0);
}

/**
 * The modules and DTCs the switched-on filters let through. A module whose DTCs are all filtered out is
 * left out too.
 *
 * @param modules - the modules of the read
 * @param filters - which filters are switched on
 * @returns the modules that still have a DTC to show, with only those DTCs
 */
export function filterDtcModules(modules: readonly Ssm5DtcModule[], filters: Ssm5DtcFilters): Ssm5DtcModule[] {
    return modules
        .map((module) => ({ ...module, dtcs: module.dtcs.filter((dtc) => isDtcShown(dtc, filters)) }))
        .filter((module) => module.dtcs.length > 0);
}

/**
 * Whether no switched-off filter hides the DTC.
 *
 * @param dtc - the DTC
 * @param filters - which filters are switched on
 * @returns true when the DTC shows
 */
function isDtcShown(dtc: Ssm5Dtc, filters: Ssm5DtcFilters): boolean {
    return DTC_FILTERS.every((filter) => filters[filter.key] || !filter.hides(dtc));
}
